import React, { useEffect, useId, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  EXTRA_DEFS, INSIGHT_TYPES, RAMP_METHOD_TEXT, aboutData, aboutRampMethods, insightDef, paramsInForce, rampDefinition,
} from '../../constants/insightsText';
import { ResizeHandle, useResizableWidth } from './useResizableWidth';

const MINUS = '−';

// Texts that state a number are functions of the params in force.
const inForce = (v, params) => (typeof v === 'function' ? v(params) : v);

const RAMP_TYPES = ['ramp_down', 'ramp_up'];
const SUBTLE = 'text-[12.5px] text-gray-500 dark:text-gray-300';

// `method`: the ramp method a ramp section follows, named under its title.
function Section({ def, params, method }) {
  const thresholds = def.thresholds ? def.thresholds(params) : [];
  const meaning = inForce(def.meaning, params);
  const formula = inForce(def.formula, params) || [];
  return (
    <section className="py-3 border-b border-gray-200/70 dark:border-gray-600/70">
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{def.name}</h4>
      {method && <p className="text-xs text-gray-500 dark:text-gray-300">{method} method</p>}
      <p className="mt-1 text-[13px] leading-relaxed text-gray-700 dark:text-gray-300">{meaning}</p>
      {formula.length > 0 && (
        <>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-300">How it is calculated</p>
          <pre className="mt-1 whitespace-pre-wrap rounded-md bg-gray-50 dark:bg-gray-800 px-2.5 py-2 font-mono text-[11.5px] text-gray-900 dark:text-gray-100">
            {formula.join('\n').replace(/ - /g, ` ${MINUS} `)}
          </pre>
        </>
      )}
      {thresholds.length > 0 && (
        <>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-300">Thresholds</p>
          <table className="mt-1 w-full text-[12.5px]">
            <tbody>
              {thresholds.map(([label, value]) => (
                <tr key={label}>
                  <th className="w-2/5 py-0.5 pr-2 text-left align-top font-medium text-gray-500 dark:text-gray-300">{label}</th>
                  <td className="py-0.5 text-gray-900 dark:text-gray-100">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {def.not && (
        <p className="mt-2 border-l-2 border-gray-200 dark:border-gray-600 pl-2 text-[12.5px] text-gray-700 dark:text-gray-300">
          <span className="font-semibold">What it does not mean. </span>{def.not}
        </p>
      )}
    </section>
  );
}

// The two ramp methods, the one in use marked, and where they differ.
function RampMethods({ about }) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="pb-3 border-b border-gray-200/70 dark:border-gray-600/70">
      <h4 id={headingId} className="text-sm font-semibold text-gray-900 dark:text-gray-100">Two ways to find ramps</h4>
      <p className={`mt-1 ${SUBTLE}`}>{about.lead}</p>
      {about.methods.map((m) => (
        <div
          key={m.key}
          data-testid={`ramp-method-${m.key}`}
          className={`mt-2 rounded-md border px-3 py-2 ${m.inUse ? 'border-brand-300 dark:border-brand-700' : 'border-gray-200/70 dark:border-gray-600/70'}`}
        >
          <p className="flex items-center gap-2 text-[13px] font-semibold text-gray-900 dark:text-gray-100">
            {m.name}
            {m.inUse && (
              <span className="rounded-full bg-brand-50 dark:bg-brand-900/30 px-1.5 py-px text-[11px] font-medium text-brand-700 dark:text-brand-300">In use</span>
            )}
          </p>
          <p className="mt-0.5 text-[12.5px] text-gray-700 dark:text-gray-300">{m.question}</p>
          <table className="mt-1.5 w-full text-[12.5px]">
            <tbody>
              {m.rows.map(([label, value]) => (
                <tr key={label}>
                  <th className="w-1/4 py-0.5 pr-2 text-left align-top font-medium text-gray-500 dark:text-gray-300">{label}</th>
                  <td className="py-0.5 text-gray-900 dark:text-gray-100">{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {m.note && <p className={`mt-1 ${SUBTLE}`}>{m.note}</p>}
        </div>
      ))}
      <p className="mt-3 text-xs text-gray-500 dark:text-gray-300">Where they differ</p>
      {about.differ.map((line) => (
        <p key={line} className="mt-1 text-[13px] leading-relaxed text-gray-700 dark:text-gray-300">{line}</p>
      ))}
    </section>
  );
}

// The two tabs (task 14): the indicators, and what counts as a ramp.
const TABS = [
  { key: 'indicators', label: 'Indicators' },
  { key: 'ramp', label: 'What counts as a ramp' },
];

// noForecastRamps: the loaded data hold no forecast-based ramp pointers.
// tab: the tab it opens on; a new tabKey asks for it again while open.
export default function AboutInsightsPanel({
  open, onClose, params, rampMethod, noForecastRamps = false, tab: openTab = 'indicators', tabKey,
}) {
  const closeRef = useRef(null);
  const asideRef = useRef(null);
  const wrapRef = useRef(null);
  const tabRefs = useRef([]);
  const baseId = useId();
  const [tab, setTab] = useState('indicators');
  // The panel keeps its right edge and widens towards the left, at most to
  // the chart card's width less a margin (the card is the parent).
  const resize = useResizableWidth({
    storageKey: 'insightsAboutWidth',
    min: 320,
    initial: 420,
    max: () => (wrapRef.current?.parentElement?.clientWidth || 1200) - 24,
  });
  // The panel can open before any insights load: fall back to the defaults
  // and the ramp method selected in the menu.
  const p = paramsInForce(params ?? (rampMethod ? { ramp_method: rampMethod } : undefined));

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // It opens from the Insights menu, which closes and takes the focus with
  // it, so keyboard users land on the panel's close button. It opens on the
  // tab asked for: Indicators from the menu, the ramp tab from the Observed strip.
  useEffect(() => {
    if (!open) return;
    setTab(openTab);
    closeRef.current?.focus();
  }, [open, openTab, tabKey]);

  // A tab starts at its top, whatever the other was scrolled to.
  const select = (i) => {
    setTab(TABS[i].key);
    tabRefs.current[i]?.focus();
    if (asideRef.current) asideRef.current.scrollTop = 0;
  };
  // Arrow keys, Home and End move between the tabs (WAI-ARIA tabs pattern).
  const onTabKey = (e, i) => {
    const next = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: TABS.length - 1 }[e.key];
    if (next == null) return;
    e.preventDefault();
    select((next + TABS.length) % TABS.length);
  };

  if (!open) return null;
  return (
    <div ref={wrapRef} data-testid="about-insights-panel" className="absolute inset-y-0 right-0 z-40 max-w-full" style={{ width: resize.width }}>
      <ResizeHandle resize={resize} edge="left" />
      <aside
        ref={asideRef}
        aria-label="About these indicators"
        className="h-full w-full overflow-auto bg-white dark:bg-gray-870 border-l border-gray-200/70 dark:border-gray-600/70 shadow-xl px-5 pb-4"
      >
        {/* Stays on top, so the panel closes and switches tabs from anywhere; the
            tab row's border separates it from the content scrolling under it. */}
        <header data-testid="about-insights-header" className="sticky top-0 z-10 -mx-5 px-5 pt-4 bg-white dark:bg-gray-870">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">About these indicators</h3>
            <button
              ref={closeRef}
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="rounded-md border border-gray-200/70 dark:border-gray-600/70 p-1 text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
          <div role="tablist" aria-label="About these indicators" className="mt-3 grid grid-cols-2 border-b border-gray-200/70 dark:border-gray-600/70">
            {TABS.map((t, i) => {
              const on = t.key === tab;
              return (
                <button
                  key={t.key}
                  ref={(el) => { tabRefs.current[i] = el; }}
                  type="button"
                  role="tab"
                  id={`${baseId}-tab-${t.key}`}
                  aria-selected={on}
                  aria-controls={`${baseId}-panel-${t.key}`}
                  tabIndex={on ? 0 : -1}
                  onClick={() => select(i)}
                  onKeyDown={(e) => onTabKey(e, i)}
                  className={`-mb-px h-9 border-b-2 text-[13px] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${
                    on ? 'border-brand-600 font-semibold text-brand-700 dark:border-brand-400 dark:text-brand-300' : 'border-transparent font-medium text-gray-500 dark:text-gray-300'
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </header>
        <div
          role="tabpanel"
          id={`${baseId}-panel-indicators`}
          aria-labelledby={`${baseId}-tab-indicators`}
          hidden={tab !== 'indicators'}
          tabIndex={0}
          className="pt-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <RampMethods about={aboutRampMethods(p, { noForecastRamps })} />
          {INSIGHT_TYPES.map((type) => (
            <Section
              key={type}
              def={insightDef(type, p)}
              params={p}
              method={RAMP_TYPES.includes(type) ? RAMP_METHOD_TEXT[p.ramp_method === 'forecast' ? 'forecast' : 'crowd'].name : null}
            />
          ))}
          {EXTRA_DEFS.map((def) => <Section key={def.key} def={def} params={p} />)}
          <section className="py-3">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Data and timing</h4>
            {aboutData(p).map((line) => (
              <p key={line} className="mt-1 text-[13px] leading-relaxed text-gray-700 dark:text-gray-300">{line}</p>
            ))}
          </section>
        </div>
        <div
          role="tabpanel"
          id={`${baseId}-panel-ramp`}
          aria-labelledby={`${baseId}-tab-ramp`}
          hidden={tab !== 'ramp'}
          tabIndex={0}
          className="pt-3 space-y-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 text-[13px] leading-relaxed text-gray-700 dark:text-gray-300"
        >
          {rampDefinition(p.observed_rule, { loaded: params != null }).map((line) => <p key={line}>{line}</p>)}
        </div>
      </aside>
    </div>
  );
}
